import React from 'react';
import {
    Snackbar,
    Alert,
    AlertTitle,
    IconButton,
    Box,
    Typography,
    Button,
    Collapse
} from '@mui/material';
import {
    CloseIcon,
    ErrorIcon,
    WarningIcon,
    InfoIcon,
    CheckCircleIcon
} from 'icons/materialIcons';
import { useError } from '../../contexts/ErrorContext';

const GlobalErrorNotification = () => {
    const {
        errors,
        removeError,
        clearErrors,
        isErrorModalOpen,
        currentError,
        closeErrorModal,
        ERROR_SEVERITY
    } = useError();

    // Get the most recent error to display
    const latestError = errors.length > 0 ? errors[errors.length - 1] : null;

    // Additional filtering to prevent certain errors from showing
    const shouldShowError = (error) => {
        if (!error) return false;

        // Don't show errors that are already being handled by local components
        if (error.source === 'api' && error.message?.includes('timeout')) {
            return false;
        }

        // Don't show generic network errors
        if (error.message?.includes('Network Error') || error.message?.includes('ERR_NETWORK')) {
            return false;
        }

        // Don't show server errors (500+)
        if (error.originalError?.response?.status >= 500) {
            return false;
        }

        // Don't show connection refused errors
        if (error.message?.includes('connection refused') || error.message?.includes('ECONNREFUSED')) {
            return false;
        }

        return true;
    };

    // Get the most recent error that should be shown
    const displayError = errors.length > 0 ?
        errors.slice().reverse().find(shouldShowError) : null;

    // Determine alert severity based on error severity
    const getAlertSeverity = (severity) => {
        switch (severity) {
            case ERROR_SEVERITY.CRITICAL:
                return 'error';
            case ERROR_SEVERITY.HIGH:
                return 'error';
            case ERROR_SEVERITY.MEDIUM:
                return 'warning';
            case ERROR_SEVERITY.LOW:
                return 'info';
            default:
                return 'error';
        }
    };

    // Get icon based on error severity
    const getIcon = (severity) => {
        switch (severity) {
            case ERROR_SEVERITY.CRITICAL:
            case ERROR_SEVERITY.HIGH:
                return <ErrorIcon />;
            case ERROR_SEVERITY.MEDIUM:
                return <WarningIcon />;
            case ERROR_SEVERITY.LOW:
                return <InfoIcon />;
            default:
                return <ErrorIcon />;
        }
    };

    // Handle close notification
    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        if (displayError) {
            removeError(displayError.id);
        }
    };

    // Handle close modal
    const handleCloseModal = () => {
        closeErrorModal();
    };

    // Handle retry (if error is retryable)
    const handleRetry = () => {
        if (displayError?.retryable && displayError?.onRetry) {
            displayError.onRetry();
        }
        removeError(displayError.id);
    };

    // Handle clear all errors
    const handleClearAll = () => {
        clearErrors();
    };

    // Don't render if no errors to display
    if (!displayError && !isErrorModalOpen) {
        return null;
    }

    return (
        <>
            {/* Snackbar for non-critical errors */}
            {displayError && displayError.severity !== ERROR_SEVERITY.CRITICAL && (
                <Snackbar
                    open={!!displayError}
                    autoHideDuration={displayError.autoDismiss ? 4000 : 8000}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    sx={{ zIndex: 9999 }}
                >
                    <Alert
                        onClose={handleClose}
                        severity={getAlertSeverity(displayError.severity)}
                        icon={getIcon(displayError.severity)}
                        action={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {displayError.retryable && displayError.onRetry && (
                                    <Button
                                        color="inherit"
                                        size="small"
                                        onClick={handleRetry}
                                        sx={{ mr: 1 }}
                                    >
                                        Retry
                                    </Button>
                                )}
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={handleClose}
                                >
                                    <CloseIcon fontSize="inherit" />
                                </IconButton>
                            </Box>
                        }
                        sx={{
                            minWidth: 300,
                            maxWidth: 400,
                            '& .MuiAlert-message': {
                                width: '100%'
                            }
                        }}
                    >
                        <AlertTitle sx={{ fontWeight: 'bold' }}>
                            {displayError.source ? `${displayError.source.charAt(0).toUpperCase() + displayError.source.slice(1)} Error` : 'Error'}
                        </AlertTitle>
                        <Typography variant="body2">
                            {displayError.message}
                        </Typography>
                        {errors.length > 1 && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    {errors.length - 1} more error{errors.length > 2 ? 's' : ''}...
                                </Typography>
                                <Button
                                    size="small"
                                    onClick={handleClearAll}
                                    sx={{ ml: 1, textTransform: 'none' }}
                                >
                                    Clear All
                                </Button>
                            </Box>
                        )}
                    </Alert>
                </Snackbar>
            )}

            {/* Modal for critical errors */}
            {isErrorModalOpen && currentError && (
                <Snackbar
                    open={isErrorModalOpen}
                    anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
                    sx={{ zIndex: 10000 }}
                >
                    <Alert
                        severity="error"
                        icon={<ErrorIcon />}
                        sx={{
                            minWidth: 400,
                            maxWidth: 600,
                            '& .MuiAlert-message': {
                                width: '100%'
                            }
                        }}
                    >
                        <AlertTitle sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                            Critical Error
                        </AlertTitle>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            {currentError.message}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {currentError.retryable && currentError.onRetry && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        currentError.onRetry();
                                        handleCloseModal();
                                    }}
                                    size="small"
                                >
                                    Retry
                                </Button>
                            )}
                            <Button
                                variant="outlined"
                                onClick={handleCloseModal}
                                size="small"
                            >
                                Close
                            </Button>
                            {errors.length > 1 && (
                                <Button
                                    variant="text"
                                    onClick={handleClearAll}
                                    size="small"
                                >
                                    Clear All Errors
                                </Button>
                            )}
                        </Box>
                    </Alert>
                </Snackbar>
            )}
        </>
    );
};

export default GlobalErrorNotification; 